import { useState, useEffect } from 'react';
import { getSites, createSite, updateSite, deleteSite } from '../utils/api';

export const useSites = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSites = async () => {
    try {
      setLoading(true);
      setError(null);
      const sitesData = await getSites();
      // Ensure sitesData is an array and contains valid site objects
      if (Array.isArray(sitesData)) {
        setSites(sitesData.filter(site => 
          site && typeof site === 'object' && 
          typeof site.id === 'string' && 
          typeof site.name === 'string'
        ));
      } else {
        throw new Error('Invalid sites data received from API');
      }
    } catch (err) {
      // Ensure error is always a string
      const errorMessage = typeof err === 'object' && err !== null
        ? (err.message || err.error || JSON.stringify(err))
        : String(err);
      setError(errorMessage);
      setSites([]); // Don't override with hardcoded sites on error
    } finally {
      setLoading(false);
    }
  };

  const createNewSite = async (siteData) => {
    try {
      const newSite = await createSite(siteData);
      setSites(prev => [...prev, newSite]);
      return newSite;
    } catch (err) {
      // Ensure error is always a string
      const errorMessage = typeof err === 'object' && err !== null
        ? (err.message || err.error || JSON.stringify(err))
        : String(err);
      setError(errorMessage);
      throw err;
    }
  };

  const updateExistingSite = async (siteId, siteData) => {
    try {
      const updatedSite = await updateSite(siteId, siteData);
      setSites(prev => prev.map(site => 
        site.id === siteId ? { ...site, ...updatedSite } : site
      ));
      return updatedSite;
    } catch (err) {
      // Ensure error is always a string
      const errorMessage = typeof err === 'object' && err !== null
        ? (err.message || err.error || JSON.stringify(err))
        : String(err);
      setError(errorMessage);
      throw err;
    }
  };

  const removesite = async (siteId) => {
    try {
      await deleteSite(siteId);
      setSites(prev => prev.filter(site => site.id !== siteId));
    } catch (err) {
      // Ensure error is always a string
      const errorMessage = typeof err === 'object' && err !== null
        ? (err.message || err.error || JSON.stringify(err))
        : String(err);
      setError(errorMessage);
      throw err;
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  return {
    sites,
    loading,
    error,
    refetch: fetchSites,
    createSite: createNewSite,
    updateSite: updateExistingSite,
    deleteSite: removesite
  };
};